import { useState, useEffect } from 'react'

export interface Project {
  id: number
  title: string
  category: string
  shortDescription: string
  description: string
  client: string
  year: string
  tools: string[]
  images: string[]
  thumbnail?: string
  slug: string
}

const projectFiles = [
  'beigua-trophy.json',
  'dui-brand-system.json',
  'genova-pra-skatepark.json',
  'typography-architecture.json',
  'lavazza-professional.json',
  'juventus.json',
  'ove.json',
  'fpt.json',
  'distinto.json',
  'photo.json'
]

// All project slugs (including those without JSON files - hardcoded pages)
const allProjectSlugs = [
  'dui-brand-system',
  'juventus',
  'ove',
  'fpt',
  'distinto',
  'photo',
  'beigua-trophy',
  'genova-pra-skatepark',
  'typography-architecture',
  'lavazza-professional',
  'lavazza-bilanci',
  'tribute-discovery-daft-punk'
]

// Progetti senza JSON - devono essere aggiunti manualmente
const hardcodedProjects: Project[] = [
  {
    id: 0,
    title: 'Lavazza Bilanci',
    category: 'Graphic Design',
    shortDescription: 'Visual communication project for Lavazza Bilanci',
    description: 'Visual communication project for Lavazza Bilanci. The work explores two complementary directions: the Civilistico and Sostenibile collections.',
    client: 'Lavazza',
    year: '2017',
    tools: ['Graphic Design', 'Photography'],
    images: [],
    thumbnail: '/assets/projects/lavazza_bilanci/cover_sostenibile_circle_3.jpg',
    slug: 'lavazza-bilanci'
  },
  {
    id: 0,
    title: 'Tribute Discovery - Daft Punk',
    category: 'Motion Design',
    shortDescription: 'A tribute project celebrating Daft Punk',
    description: 'A tribute project celebrating Daft Punk\'s iconic album "Discovery" through motion graphics and 3D.',
    client: 'Personal Project',
    year: '2024',
    tools: ['Motion Design', '3D'],
    images: [],
    thumbnail: '/assets/projects/tribute-discovery-daft-punk/interstella5555_copertina.jpg',
    slug: 'tribute-discovery-daft-punk'
  }
]

// Get seen projects from sessionStorage
const getSeenProjects = (): Set<string> => {
  if (typeof window === 'undefined') return new Set()
  const stored = sessionStorage.getItem('seenProjects')
  return stored ? new Set(JSON.parse(stored)) : new Set()
}

// Save seen projects to sessionStorage
const saveSeenProjects = (seen: Set<string>) => {
  if (typeof window === 'undefined') return
  sessionStorage.setItem('seenProjects', JSON.stringify([...seen]))
}

export function useProjects() {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const loadedProjects = await Promise.all(
          projectFiles.map(async (file) => {
            try {
              const response = await fetch(`/content/projects/${file}`)
              if (!response.ok) return null
              return await response.json()
            } catch {
              return null
            }
          })
        )
        // Combina i progetti caricati da JSON con quelli hardcoded
        const jsonProjects = loadedProjects.filter((p): p is Project => p !== null)
        let allProjects = [...jsonProjects, ...hardcodedProjects]
        
        // Swap OVE and Lavazza Bilanci positions
        const oveIndex = allProjects.findIndex(p => p.slug === 'ove')
        const lavazzaBilanciIndex = allProjects.findIndex(p => p.slug === 'lavazza-bilanci')
        
        if (oveIndex !== -1 && lavazzaBilanciIndex !== -1) {
          // Swap the two projects
          const temp = allProjects[oveIndex]
          allProjects[oveIndex] = allProjects[lavazzaBilanciIndex]
          allProjects[lavazzaBilanciIndex] = temp
        }
        
        setProjects(allProjects)
      } catch (error) {
        console.error('Error loading projects:', error)
        // In caso di errore, usa almeno i progetti hardcoded
        setProjects(hardcodedProjects)
      } finally {
        setLoading(false)
      }
    }

    loadProjects()
  }, [])

  const getProjectBySlug = (slug: string) => {
    return projects.find(p => p.slug === slug)
  }

  const getNextProject = (currentSlug: string) => {
    const currentIndex = projects.findIndex(p => p.slug === currentSlug)
    if (currentIndex === -1) return null
    const nextIndex = (currentIndex + 1) % projects.length
    return projects[nextIndex]
  }

  const getPrevProject = (currentSlug: string) => {
    const currentIndex = projects.findIndex(p => p.slug === currentSlug)
    if (currentIndex === -1) return null
    const prevIndex = currentIndex === 0 ? projects.length - 1 : currentIndex - 1
    return projects[prevIndex]
  }

  const getRandomProject = (currentSlug: string): Project | null => {
    // Get all available projects (slugs that exist)
    const availableSlugs = allProjectSlugs.filter(slug => slug !== currentSlug)
    
    // Debug: log available slugs
    console.log('Current slug:', currentSlug)
    console.log('Available slugs:', availableSlugs)
    console.log('Available count:', availableSlugs.length)

    if (availableSlugs.length === 0) return null
    
    if (availableSlugs.length === 1) {
      // Only one project left, return it directly
      const slug = availableSlugs[0]
      const project = projects.find(p => p.slug === slug)
      if (project) return project
      return {
        id: 0,
        title: slug,
        category: '',
        shortDescription: '',
        description: '',
        client: '',
        year: '',
        tools: [],
        images: [],
        slug: slug
      }
    }

    // Get seen projects from sessionStorage
    const seenProjects = getSeenProjects()
    console.log('Seen projects:', [...seenProjects])
    
    // Filter out seen projects
    let candidates = availableSlugs.filter(slug => !seenProjects.has(slug))
    console.log('Candidates:', candidates)
    
    // If all projects have been seen, reset and start fresh
    if (candidates.length === 0) {
      console.log('All projects seen, resetting...')
      sessionStorage.removeItem('seenProjects')
      candidates = availableSlugs
    }

    // Pick a random slug from candidates
    const randomSlug = candidates[Math.floor(Math.random() * candidates.length)]
    console.log('Selected random slug:', randomSlug)
    
    // Mark this project as seen
    seenProjects.add(randomSlug)
    saveSeenProjects(seenProjects)

    // Try to find the project in loaded projects
    const project = projects.find(p => p.slug === randomSlug)
    console.log('Found project:', project)
    if (project) return project

    // If not found in JSON, return a minimal project object with just the slug
    // The page will handle it via hash routing
    return {
      id: 0,
      title: randomSlug,
      category: '',
      shortDescription: '',
      description: '',
      client: '',
      year: '',
      tools: [],
      images: [],
      slug: randomSlug
    }
  }

  // Reset the seen projects
  const resetSeenProjects = () => {
    sessionStorage.removeItem('seenProjects')
  }

  return { projects, loading, getProjectBySlug, getNextProject, getPrevProject, getRandomProject, resetSeenProjects }
}
